import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Grid from "@mui/material/Unstable_Grid2"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { format, isValid, parse } from "date-fns"

import Row from "../../common/components/Row"
import ChipsInput from "../components/ChipsInput"
import ChoiceMultiSelect from "../components/ChoiceMultiSelect"
import NumberValueInput from "../components/NumberValueInput"
import {
  FIELD_KINDS,
  OPERATORS,
  VALUE_SHAPES,
  convertValueForOperator,
  fieldSpec,
  isFilterComplete,
  operatorLabel,
  operatorValueShape,
  operatorsForField,
} from "../filterSchema"

const DATE_FORMAT = "yyyy-MM-dd"

function parseFilterDate(value) {
  if (!value) {
    return null
  }
  // Older filters were saved without zero padding ("2026-10-5").
  const date = parse(String(value), "yyyy-M-d", new Date())
  return isValid(date) ? date : null
}

function formatFilterDate(date) {
  return date && isValid(date) ? format(date, DATE_FORMAT) : null
}

/**
 * One filter row's operator and value, driven by the field's entry in
 * FIELD_SPECS. `choices` ([{value, label}]) makes a pick-from-menu field;
 * `suggestions` (strings) are offered under a typed chips field.
 */
export default function SchemaFilterBody({
  fieldConfig,
  onConfigCompleteChanged,
  valueLabel,
  choices,
  suggestions,
  loadingChoices = false,
}) {
  const spec = fieldSpec(fieldConfig.field) || {}
  const kind = spec.kind
  const label = valueLabel || spec.label
  const type = fieldConfig.type
  const shape = operatorValueShape(type)

  function update(changes) {
    const next = { ...fieldConfig, ...changes }
    onConfigCompleteChanged(isFilterComplete(next), next)
  }

  function handleOperatorChanged(nextType) {
    update({
      type: nextType,
      value: convertValueForOperator(
        fieldConfig.field,
        type,
        nextType,
        fieldConfig.value
      ),
    })
  }

  function setValue(value) {
    update({ value })
  }

  if (kind === FIELD_KINDS.BOOLEAN) {
    return (
      <Grid xs={12}>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={
            typeof fieldConfig.value === "boolean" ? fieldConfig.value : null
          }
          onChange={(_event, value) => {
            if (typeof value === "boolean") {
              update({ type: OPERATORS.IS, value })
            }
          }}
          aria-label={label}
        >
          <ToggleButton value={true}>Yes</ToggleButton>
          <ToggleButton value={false}>No</ToggleButton>
        </ToggleButtonGroup>
      </Grid>
    )
  }

  const operators = operatorsForField(fieldConfig.field, type)

  return (
    <>
      <Grid xs={12} sm={4}>
        <TextField
          select
          size="small"
          label="Condition"
          value={type || ""}
          onChange={(event) => handleOperatorChanged(event.target.value)}
          sx={{ width: "100%" }}
        >
          {operators.map((operator) => (
            <MenuItem key={operator} value={operator}>
              {operatorLabel(fieldConfig.field, operator)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid xs={12} sm={8}>
        <ValueInput
          kind={kind}
          spec={spec}
          shape={shape}
          label={label}
          value={fieldConfig.value}
          onChange={setValue}
          choices={choices}
          suggestions={suggestions}
          loadingChoices={loadingChoices}
        />
      </Grid>
    </>
  )
}

function ValueInput({
  kind,
  spec,
  shape,
  label,
  value,
  onChange,
  choices,
  suggestions,
  loadingChoices,
}) {
  if (shape === VALUE_SHAPES.NONE) {
    return null
  }

  if (kind === FIELD_KINDS.DATE) {
    if (shape === VALUE_SHAPES.RANGE) {
      const [start, end] = Array.isArray(value) ? value : [null, null]
      return (
        <Row sx={{ gap: 1 }}>
          <DatePicker
            label="From"
            value={parseFilterDate(start)}
            onChange={(date) => onChange([formatFilterDate(date), end ?? null])}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="To"
            value={parseFilterDate(end)}
            minDate={parseFilterDate(start) || undefined}
            onChange={(date) =>
              onChange([start ?? null, formatFilterDate(date)])
            }
            slotProps={{ textField: { size: "small" } }}
          />
        </Row>
      )
    }
    return (
      <DatePicker
        label={label}
        value={parseFilterDate(value)}
        onChange={(date) => onChange(formatFilterDate(date))}
        slotProps={{ textField: { size: "small", fullWidth: true } }}
      />
    )
  }

  if (kind === FIELD_KINDS.NUMBER) {
    const numberProps = { money: !!spec.money, whole: !!spec.whole }
    if (shape === VALUE_SHAPES.RANGE) {
      const [low, high] = Array.isArray(value) ? value : [null, null]
      return (
        <Row sx={{ gap: 1 }}>
          <NumberValueInput
            label="Min"
            value={low}
            onChange={(n) => onChange([n, high ?? null])}
            {...numberProps}
          />
          <NumberValueInput
            label="Max"
            value={high}
            onChange={(n) => onChange([low ?? null, n])}
            {...numberProps}
          />
        </Row>
      )
    }
    return (
      <NumberValueInput
        label={label}
        value={value}
        onChange={onChange}
        helperText={spec.hint}
        {...numberProps}
      />
    )
  }

  if (choices) {
    return (
      <ChoiceMultiSelect
        label={label}
        choices={choices}
        value={value}
        onChange={onChange}
        loading={loadingChoices}
      />
    )
  }

  if (shape === VALUE_SHAPES.LIST) {
    return (
      <ChipsInput
        label={label}
        placeholder="Type, then comma or Enter"
        value={value}
        onChange={onChange}
        suggestions={suggestions}
      />
    )
  }

  // A single text value: only legacy operators reach this.
  return (
    <TextField
      size="small"
      label={label}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      sx={{ width: "100%" }}
    />
  )
}

/** A filter body for `fieldName` with fixed extra props (label, choices...). */
export function schemaFilterBody(extraProps = {}) {
  function Body(props) {
    return <SchemaFilterBody {...props} {...extraProps} />
  }
  return Body
}
